import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";

const MAX_POLLS = 15;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | expired | failed | error
  const [detail, setDetail] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setDetail(data);
          checkAuth?.();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus(data.payment_status);
          return;
        }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) {
          setStatus("error");
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        polls.current += 1;
        if (polls.current >= MAX_POLLS) setStatus("error");
        else setTimeout(tick, 2000);
      }
    };
    tick();
    return () => { cancelled = true; };
     
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F1EE] px-6">
      <div className="w-full max-w-md text-center">
        {status === "polling" && (
          <div data-testid="payment-polling">
            <Loader2 className="w-10 h-10 text-[#1A362D] animate-spin mx-auto" />
            <h1 className="font-display text-3xl mt-6 text-[#1A362D]">Confirming your payment…</h1>
            <p className="text-[#5C6B64] mt-2 text-sm">This usually takes a few seconds.</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-success">
            <CheckCircle2 className="w-12 h-12 text-[#1A362D] mx-auto" />
            <h1 className="font-display text-4xl mt-6 text-[#1A362D]">Welcome to Pro.</h1>
            <p className="text-[#5C6B64] mt-3">
              You're all set{detail?.lookup_key === "pro_lifetime" ? " — for life" : ""}. Kanban, Analytics, and every export are unlocked.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="mt-8 rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white h-12 px-6"
              data-testid="go-dashboard-btn"
            >
              Open dashboard <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        )}
        {(status === "expired" || status === "failed" || status === "error") && (
          <div data-testid="payment-failed">
            <h1 className="font-display text-4xl text-[#C65D47]">Payment didn't complete</h1>
            <p className="text-[#5C6B64] mt-3">
              {status === "expired" ? "Your checkout session expired." : "Something didn't go through."}
              {" "}You can try again — no charge was captured.
            </p>
            <div className="mt-8 flex justify-center gap-3">
              <Button asChild variant="outline" className="rounded-full h-12"><Link to="/dashboard">Back to dashboard</Link></Button>
              <Button asChild className="rounded-full h-12 bg-[#1A362D] hover:bg-[#2C4A40] text-white"><Link to="/upgrade">Try again</Link></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
